import { KnownStatusType } from '@mm/components';
import SuggestionModel from '../model/SuggestionModel';
import { Pagination } from '../store-interfaces';
import { VoyagerKeyType } from './voyagerLeads';

export type TopDialogue = {
  id: number;
  name: string;
  notUniqViewsCount: null | number;
  sessionsStartedCount: null | number;
  viewsCount: null | number;
  responsesCount: number;
  notUniqResponsesCount: number;
  folder: string;
};

export type ContactsByPlatformType = {
  facebook: number[];
  sms: number[];
  webChat: number[];
  instagram: number[];
  dates: string[];
  totals: {
    facebook: number;
    sms: number;
    webChat: number;
    instagram: number;
  };
};

export type ContactsBySourceType = {
  facebookOptIns?: number;
  customerChatWidget?: number;
  messengerAd?: number;
  optInPopups?: number;
  shareLinks?: number;
  commentGuards?: number;
  igWelcomer?: number;
  igCommentGuards?: number;
  igStoryMentions?: number;
  landingPage?: number;
  contactsCsv?: number;
  adConfig?: number;
  fbPageWelcomer?: number;
  smsKeyword?: number;
  page?: number;
  trigger?: number;
  iceBreaker?: number;
  unknown?: number;
};

export type QAKeywords = {
  name: string;
  firedCount: number;
};

export type EmailAnalyticsType = Partial<
  Record<EmailFilterTabsType, EmailStatsPayload>
>;

export type XRayAnalyticsStoreType = Partial<
  Record<XRayAnalyticsType, XRayAnalyticsPayload>
>;

export type ContactsSourcesType = {
  csv: number;
  facebook: number;
  instagram: number;
  xray: number;
  leadsDb: number;
  webChat: number;
  sms: number;
};

export type DemographicAnalyticsPayload = Pick<
  XRayAnalyticsPayload,
  'hits' | 'total'
> & { records: string[] };

export type DemographicAnalyticsKeyType = VoyagerKeyType | 'age' | 'gender';

export type DemographicAnalyticsType = Partial<
  Record<DemographicAnalyticsKeyType, DemographicAnalyticsPayload>
>;

export type DemographicAnalyticsCategory = 'individual' | 'location' | 'income';

export type ContactsGatheredResponseType = {
  contactsGathered: {
    [key: number]: number;
  };
  total: number;
};

export type BotAnalytics = {
  busy: boolean;
  topDialogues: {
    dialogues: TopDialogue[];
    pagination: Pagination;
  };
  contactsAmountAnalytics: {
    totalContacts: number[];
    sessions: number[];
    totalNewContacts: number[];
    date: string[];
    messagesCount: number[];
  };
  unansweredQuestions: SuggestionModel[];
  qaKeywords: QAKeywords[];
  contactsByPlatform: ContactsByPlatformType;
  contactsBySource: ContactsBySourceType;
  contactsByPlatformAggregated: ContactsByPlatformType;
  emailAnalyticsByTab: EmailAnalyticsType;
  xRayAnalytics: XRayAnalyticsStoreType;
  contactsSources: ContactsSourcesType | null;
  demographicAnalytics:
    | {
        [key in DemographicAnalyticsCategory]: DemographicAnalyticsType;
      }
    | null;
  contactsGathered: ContactsGatheredResponseType | null;
};

export type EmailStatsPayload = {
  stats: EmailStatsEntry[];
  pagination: Pagination;
};

export type XRayAnalyticsPayload = {
  dates?: string[];
  hits?: number[];
  total?: number;
  emailDomain?: string[];
  landingPageUrl?: string[];
  avgLeadScores?: {
    webPageViews?: null | number;
    scrollDepth?: null | number;
    numOfClicks?: null | number;
    timeOnSite?: null | number;
    supressedContacts?: null | number;
  };
  wday?: number[];
  views: string[];
  timeOnSite: string[];
  clicks: string[];
  webSessionsCount: string[];
  supressedContacts: {
    count: number;
  };
};

export const EMAIL_FILTER_TABS = [
  'automations',
  'sender_emails',
  'sender_domains',
  'by_automation',
  'by_widget',
  'summary',
] as const;

export type EmailFilterTabsType = typeof EMAIL_FILTER_TABS[number];
export type EmailAnalyticsParams = {
  botId: number;
  tab: EmailFilterTabsType;
  campaignId?: number;
  widgetDataEmailId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
};
export type AnalyticsParams = {
  botId: number;
  startDate?: string;
  endDate?: string;
  audiencesIds?: number[];
};

export const X_RAY_ANALYTICS_FILTER_TYPES = {
  contactsGathered: 'contacts_gathered',
  emailSent: 'email_sent',
  emailDomain: 'email_domain',
  wday: 'wday',
  topUrls: 'top_urls',
  timeOnSite: 'time_on_site',
  pageViews: 'page_views',
  clicks: 'clicks',
  webSessionsCount: 'web_sessions_count',
  avgLeadScores: 'avg_lead_scores',
  supressedContacts: 'supressed_contacts',
} as const;

type UIXRayAnalyticsFilterTypeTuple = typeof X_RAY_ANALYTICS_FILTER_TYPES;
export type UIXRayAnalyticsFilterType =
  UIXRayAnalyticsFilterTypeTuple[keyof UIXRayAnalyticsFilterTypeTuple];
export type XRayAnalyticsType = UIXRayAnalyticsFilterType;

type Email = string;
type Domain = string;
type Campaign = string;

export type EmailStatsEntry = Record<
  Email | Domain | Campaign,
  {
    sent?: number | null;
    open?: number | null;
    click?: number | null;
    leads?: number | null;
    remoteId?: string;
    email?: string;
    campaign?: string;
    campaignId?: number;
    senderEmail?: string;
    active?: boolean;
    status: KnownStatusType;
  }
>;
